import { type PlainWorkflow } from "@/queries/automation-queries";
import { workflowName } from "@/utils/automations";
import { Button } from "@/components/flexkit/Button";
import { Text } from "@/components/podkit/typography/Text";
import { useEffect, useRef, useState, type FC, type ReactNode } from "react";
import { withGitpodSize } from "@/assets/icons/utils";
import IconPencil from "central-icons/IconPencil";
import { Input } from "@/components/podkit/forms/Input";
import { Heading1 } from "@/components/podkit/typography/Headings";
const IconPencilWithSize = withGitpodSize(IconPencil);

type AutomationEditableHeaderProps = {
    workflow: PlainWorkflow;
    onUpdate: (name: string, description: string) => Promise<void>;
    actions?: ReactNode;
};

export const AutomationEditableHeader: FC<AutomationEditableHeaderProps> = ({ workflow, onUpdate, actions }) => {
    const [isEditing, setIsEditing] = useState(false);
    const editButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const cb = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsEditing(false);
            }
        };
        document.addEventListener("keydown", cb);
        return () => {
            document.removeEventListener("keydown", cb);
        };
    }, []);

    const closeForm = () => {
        setIsEditing(false);
    };

    if (isEditing) {
        return <EditForm workflow={workflow} onCancel={closeForm} onUpdated={closeForm} onUpdate={onUpdate} />;
    }

    return (
        <header className="group flex flex-col gap-1" data-tracking-id="default-tracking-id">
            <div className="flex flex-col items-center justify-between sm:flex-row">
                <div
                    onDoubleClick={() => {
                        // double-clicking the title text triggers edit mode
                        // click the button programmatically so that tracking works correctly
                        editButtonRef.current?.click();
                    }}
                    className="flex items-center gap-2"
                    data-tracking-id="default-tracking-id"
                >
                    <Heading1>{workflowName(workflow)}</Heading1>
                    <Button
                        ref={editButtonRef}
                        data-tracking-id="automation-edit-page-edit-automation-header-button"
                        type="button"
                        variant="outline"
                        aria-label="Edit automation name and description"
                        size="sm"
                        className="invisible group-hover:visible"
                        LeadingIcon={IconPencilWithSize}
                        onClick={() => {
                            setIsEditing(true);
                        }}
                    />
                </div>
                {actions}
            </div>
            <Text className="text-base text-content-secondary">
                {workflow.metadata?.description || "No description provided."}
            </Text>
        </header>
    );
};

type EditFormProps = {
    workflow: PlainWorkflow;
    onCancel: () => void;
    onUpdated: () => void;
    onUpdate: (name: string, description: string) => Promise<void>;
};

const EditForm: FC<EditFormProps> = ({ workflow, onCancel, onUpdated, onUpdate }) => {
    const [isUpdating, setIsUpdating] = useState(false);

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const name = formData.get("automationName");
        const description = formData.get("automationDescription");

        if (typeof name !== "string" || typeof description !== "string" || !name.trim() || !description.trim()) {
            return;
        }

        setIsUpdating(true);
        try {
            await onUpdate(name, description);
            onUpdated();
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <form className="flex flex-1 flex-col gap-2" onSubmit={onSubmit}>
            <Input
                className="max-w-none flex-1"
                disabled={isUpdating}
                name="automationName"
                defaultValue={workflowName(workflow)}
            />
            <Input
                className="max-w-none flex-1"
                disabled={isUpdating}
                name="automationDescription"
                defaultValue={workflow.metadata?.description || ""}
            />
            <div className="flex flex-row gap-2">
                <Button
                    data-tracking-id="automation-edit-page-cancel-update-automation-header-button"
                    type="button"
                    variant="ghost"
                    disabled={isUpdating}
                    onClick={onCancel}
                >
                    Cancel
                </Button>
                <Button
                    data-tracking-id="automation-edit-page-update-automation-header-button"
                    type="submit"
                    variant="secondary"
                    disabled={isUpdating}
                    loading={isUpdating}
                >
                    Save
                </Button>
            </div>
        </form>
    );
};
